import * as React from 'react'
import {
  Theme,
  createStyles,
  withStyles,
  WithStyles
} from '@material-ui/core/styles'

import Grid from '@material-ui/core/Grid'
import { TextField, Typography } from '@material-ui/core'
import PersonIcon from '@material-ui/icons/Person'
import { CustomFieldsGroup } from '../custom_fields/CustomFieldsGroup'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../OneplaceLibraryProvider'
import { IPerson } from '../../models/Person'
import { IPersonFields, IPersonFieldsConfiguration } from '../..'
import { useEffect } from 'react'

const styles = (_theme: Theme) => createStyles({})

export interface IPersonFieldsProps
  extends WithStyles<typeof styles>,
    IOneplaceLibraryContextProp {
  configuration: IPersonFieldsConfiguration // the blank template data for creating new person fields
  primaryPerson: IPerson
  personFields?: IPersonFields // the data for existing person fields
}

export default withStyles(styles)(
  withOneplaceLibraryContext((props: IPersonFieldsProps) => {
    const [personFields, setPersonFields] =
      React.useState<IPersonFields | null>()

    useEffect(() => {
      const createNewPersonFields = (person: IPerson): IPersonFields => {
        const newPersonFields = {} as IPersonFields
        newPersonFields.personId = person.id

        if (props.configuration.firstName)
          newPersonFields.firstName = person.firstName || ''
        if (props.configuration.midName)
          newPersonFields.midName = person.midName || ''
        if (props.configuration.lastName)
          newPersonFields.lastName = person.lastName || ''
        if (props.configuration.email)
          newPersonFields.email = person.email || ''
        if (props.configuration.homePhoneNumber)
          newPersonFields.homePhoneNumber = person.contact?.homePhone || ''
        if (props.configuration.mobileNumber)
          newPersonFields.mobileNumber = person.contact?.mobilePhone || ''
        if (props.configuration.workPhoneNumber)
          newPersonFields.workPhoneNumber = person.contact?.workPhone || ''
        if (props.configuration.externalId)
          newPersonFields.externalId = person.externalId || ''

        if (
          props.configuration.customFields?.length &&
          person.customFields?.length
        ) {
          const cfToBeDisplayed = props.configuration.customFields.map(
            (cf) => cf.id
          )
          const personCFToBeDisplayed = []
          // cfToBeDisplayed is ordered (CF order)
          for (const cfId of cfToBeDisplayed) {
            const personCF = person.customFields.find(
              (pcf) => pcf.customFieldId === cfId
            )
            if (personCF) personCFToBeDisplayed.push(personCF)
          }
          newPersonFields.customFields = personCFToBeDisplayed
        }
        return newPersonFields
      }

      if (props.personFields) {
        setPersonFields(props.personFields)
      } else if (props.primaryPerson && props.configuration.anyFieldSelected) {
        setPersonFields(createNewPersonFields(props.primaryPerson))
      } else {
        setPersonFields(null)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      props.personFields,
      props.primaryPerson,
      props.configuration.anyFieldSelected
    ])

    return (
      <>
        {personFields && (
          <Grid
            container
            spacing={2}
            style={{ marginBottom: 16 }}
            data-testid='person-fields'
          >
            <Grid item xs={12}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap'
                }}
              >
                <PersonIcon style={{ fontSize: '32px', marginRight: '5px' }} />
                <Typography variant='h5'>Person</Typography>
              </div>
            </Grid>
            {personFields.firstName != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  data-testid='first-name'
                  label='First Name'
                  value={personFields.firstName}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.midName != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  data-testid='mid-name'
                  label='Mid Name'
                  value={personFields.midName}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.lastName != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  data-testid='last-name'
                  label='Last Name'
                  value={personFields.lastName}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.email != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Email'
                  value={personFields.email}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.workPhoneNumber != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  data-testid='work-phone'
                  label='Work Phone Number'
                  value={personFields.workPhoneNumber}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.homePhoneNumber != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  data-testid='home-phone'
                  label='Home Phone Number'
                  value={personFields.homePhoneNumber}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.mobileNumber != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  data-testid='mobile-number'
                  label='Mobile Number'
                  value={personFields.mobileNumber}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.externalId != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  data-testid='external-id'
                  label='External ID'
                  value={personFields.externalId}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.customFields && personFields.customFields.length > 0 && (
              <Grid item xs={12} data-testid='person-custom-fields'>
                <CustomFieldsGroup
                  key={personFields.personId}
                  label=''
                  fields={personFields.customFields.map((cf) => {
                    cf.readonly = true
                    return cf
                  })}
                  helpInfos={[]}
                  onFieldChanged={() => null}
                />
              </Grid>
            )}
          </Grid>
        )}
      </>
    )
  })
)
